// src/app/core/tenant/tenant.service.ts
import { Injectable, inject } from '@angular/core';
import { ConfigService } from '../config/config.service';

export interface FinancieraTheme {
  primaryColor: string;
  primaryColorDark?: string;
  logoUrl:      string;
  name:         string;
  shortName:    string;
}

/**
 * TenantService — única fuente de verdad para el tenant y la identidad visual.
 *
 * REGLA: ningún otro archivo debe leer `Fineract-Platform-TenantId` directamente.
 * El interceptor auth.interceptor.ts llama a getTenantId() aquí.
 *
 * TODO(multi-tenant): en modo 'subdomain', extraer el tenant del hostname:
 *   const sub = window.location.hostname.split('.')[0];
 *   validar sub contra lista de tenants conocidos antes de aceptarlo.
 */
@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly config = inject(ConfigService);

  /**
   * Retorna el tenant ID para el header HTTP `Fineract-Platform-TenantId`.
   * En modo 'single': siempre es config.tenant.id (ej: "default").
   * TODO(multi-tenant): en modo 'subdomain', extraer del hostname.
   */
  getTenantId(): string {
    const tenant = this.config.get('tenant');
    // TODO(multi-tenant): implementar resolución por subdomain o param
    // cuando tenant.mode !== 'single'
    return tenant.id;
  }

  /** Nombre completo de la institución para mostrar en UI */
  getInstitutionName(): string {
    return this.config.get('institution').name;
  }

  /** Nombre corto/siglas para espacios reducidos */
  getInstitutionShortName(): string {
    return this.config.get('institution').shortName;
  }

  /** Tema visual completo — colores, logo, nombres */
  getTheme(): FinancieraTheme {
    const inst = this.config.get('institution');
    return {
      primaryColor:     inst.primaryColor,
      primaryColorDark: inst.primaryColorDark,
      logoUrl:          inst.logoUrl,
      name:             inst.name,
      shortName:        inst.shortName,
    };
  }

  /**
   * Aplica el color primario como CSS custom property en :root.
   * Llamado por AppComponent.ngOnInit() tras cargar la configuración, y por
   * ThemeService cada vez que cambia el tema resuelto (Prompt 17).
   *
   * En oscuro usa `institution.primaryColorDark` si el operador lo definió
   * en runtime-config.json; si no, sigue usando `primaryColor` — el agente
   * no deriva un color de marca sin aprobación (decisión 0.6 del Prompt 17).
   *
   * @param resolvedTheme Tema activo. Si se omite, se lee de `data-theme` en
   *   `<html>` (que theme-boot.js o ThemeService ya establecieron antes de
   *   este punto) para no depender del orden de inicialización de servicios.
   */
  applyTheme(resolvedTheme?: 'light' | 'dark'): void {
    const theme  = this.getTheme();
    const active = resolvedTheme
      ?? (document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');
    const base = active === 'dark' && theme.primaryColorDark ? theme.primaryColorDark : theme.primaryColor;

    document.documentElement.style.setProperty('--color-primary', base);
    // Calcular variantes del color primario
    document.documentElement.style.setProperty('--color-primary-dark', this.darken(base, 20));
    document.documentElement.style.setProperty('--color-primary-light', this.lighten(base, 85));
    // --color-primary-text: variante de TEXTO/ENLACE sobre la superficie del
    // tema (distinta de --color-primary-dark, que es para fondos sólidos de
    // botón y NO cambia con el tema). Oscurecer sirve como texto sobre claro;
    // sobre oscuro hay que aclarar, o cae a ~3.2:1 (medido con axe-core,
    // Fase 4) — insuficiente para texto normal.
    document.documentElement.style.setProperty(
      '--color-primary-text',
      active === 'dark' ? this.lighten(base, 20) : this.darken(base, 20),
    );
  }

  /** Oscurece un color HEX en `amount` puntos (0-255) */
  private darken(hex: string, amount: number): string {
    return this.adjustColor(hex, -amount);
  }

  /** Aclara un color HEX mezclándolo con blanco en `percent`% */
  private lighten(hex: string, percent: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const factor = percent / 100;
    const lr = Math.round(r + (255 - r) * factor);
    const lg = Math.round(g + (255 - g) * factor);
    const lb = Math.round(b + (255 - b) * factor);
    return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
  }

  private adjustColor(hex: string, amount: number): string {
    let r = parseInt(hex.slice(1, 3), 16) + amount;
    let g = parseInt(hex.slice(3, 5), 16) + amount;
    let b = parseInt(hex.slice(5, 7), 16) + amount;
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
}
